import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Card, Divider, Grid, Typography } from '@mui/material';
import MuiBreadcrumbs from '@mui/material/Breadcrumbs';

// project imports
import { BASE_PATH, DASHBOARD_PATH } from 'config';
import { gridSpacing } from 'store/constant';
import { useSelector } from 'store';

// project hooks
import useConfig from 'hooks/useConfig';

// assets
import HomeIcon from '@mui/icons-material/Home';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const linkSX = {
    display: 'flex',
    color: 'grey.900',
    textDecoration: 'none',
    alignContent: 'center',
    alignItems: 'center'
};

// ==============================|| BREADCRUMBS ||============================== //

const Breadcrumbs = ({ card, divider, maxItems, rightAlign, ...others }) => {
    const theme = useTheme();
    const { rtlLayout } = useConfig();

    const {
        hidden: breadCrumbsHidden,
        title: breadCrumbsTitle,
        separator: breadCrumbsSeparator,
        showHome: breadCrumbsShowHome,
        items: breadCrumbsItems
    } = useSelector((state) => state.breadCrumbs);

    if (breadCrumbsHidden) return '';

    const iconStyle = {
        marginRight: theme.spacing(0.75),
        marginTop: `-${theme.spacing(0.25)}`,
        width: '1rem',
        height: '1rem',
        color: theme.palette.secondary.main
    };

    return (
        <Card
            sx={{
                marginBottom: card === false ? 0 : theme.spacing(gridSpacing),
                border: card === false ? 'none' : '1px solid',
                borderColor: theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.primary[200] + 75,
                background: card === false ? 'transparent' : theme.palette.background.default
            }}
            {...others}
        >
            <Box sx={{ p: 2, pl: card === false ? 0 : 2 }}>
                <Grid
                    container
                    direction={rightAlign ? 'row' : 'column'}
                    justifyContent={rightAlign ? 'space-between' : 'flex-start'}
                    alignItems={rightAlign ? 'center' : 'flex-start'}
                    spacing={1}
                >
                    {!rtlLayout && breadCrumbsTitle && (
                        <Grid item>
                            <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                {breadCrumbsTitle}
                            </Typography>
                        </Grid>
                    )}
                    <Grid item>
                        <MuiBreadcrumbs
                            // sx={{ '& .MuiBreadcrumbs-separator': { width: 16, ml: 1.25, mr: 1.25 } }}
                            aria-label="breadcrumb"
                            maxItems={maxItems || 8}
                            separator={breadCrumbsSeparator || <ChevronRightIcon fontSize="small" />}
                        >
                            {breadCrumbsShowHome && (
                                <Typography
                                    component={Link}
                                    to={BASE_PATH + DASHBOARD_PATH}
                                    color="inherit"
                                    variant="subtitle1"
                                    sx={linkSX}
                                >
                                    <HomeIcon sx={iconStyle} />
                                </Typography>
                            )}
                            {breadCrumbsItems.map((item, index) => {
                                if (item?.url) {
                                    return (
                                        <Typography key={index} component={Link} to={BASE_PATH + item.url} variant="subtitle1" sx={linkSX}>
                                            {item?.title || ''}
                                        </Typography>
                                    );
                                }

                                return (
                                    <Typography
                                        key={index}
                                        variant="subtitle1"
                                        sx={{
                                            display: 'flex',
                                            textDecoration: 'none',
                                            alignContent: 'center',
                                            alignItems: 'center',
                                            color: 'grey.500'
                                        }}
                                    >
                                        {item?.title || ''}
                                    </Typography>
                                );
                            })}
                        </MuiBreadcrumbs>
                    </Grid>
                    {rtlLayout && breadCrumbsTitle && (
                        <Grid item>
                            <Typography variant="h3" sx={{ fontWeight: 500 }}>
                                {breadCrumbsTitle}
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            </Box>
            {card === false && divider !== false && <Divider sx={{ borderColor: theme.palette.primary.main, mb: gridSpacing }} />}
        </Card>
    );
};

Breadcrumbs.propTypes = {
    card: PropTypes.bool,
    divider: PropTypes.bool,
    maxItems: PropTypes.number,
    rightAlign: PropTypes.bool
};

export default Breadcrumbs;
